import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Camera, Edit3, Watch, Heart, Activity, Clock, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mock history data - in production this would come from API
const generateMockHistory = () => {
  const history = [];
  const sources = ['facial_rPPG', 'manual_entry', 'wearable_sync'];
  const sourceConfig = {
    'facial_rPPG': { icon: Camera, label: 'Facial Scan', color: '#C10891' },
    'manual_entry': { icon: Edit3, label: 'Manual Entry', color: '#F8B64C' },
    'wearable_sync': { icon: Watch, label: 'Wearable Sync', color: '#8BB89F' }
  };

  for (let i = 0; i < 15; i++) {
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(i / 2));
    date.setHours(date.getHours() - (i % 3) * 4);
    
    const source = sources[i % 3];
    const success = Math.random() > 0.15; // 85% success rate
    
    history.push({
      id: `scan-${i}`,
      timestamp: date.toISOString(),
      source,
      sourceConfig: sourceConfig[source],
      success,
      confidence: success ? (Math.random() > 0.5 ? 'high' : 'medium') : null,
      metrics: success ? {
        heart_rate: 60 + Math.floor(Math.random() * 30),
        hrv: 30 + Math.floor(Math.random() * 30),
        stress_index: 20 + Math.floor(Math.random() * 50)
      } : null,
      errorReason: !success ? ['Poor lighting', 'Too much movement', 'Signal quality low'][Math.floor(Math.random() * 3)] : null
    });
  }
  
  return history;
};

export default function ScanHistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, facial, manual, wearable

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setHistory(generateMockHistory());
      setLoading(false);
    }, 500);
  }, []);

  const filteredHistory = history.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'facial') return item.source === 'facial_rPPG';
    if (filter === 'manual') return item.source === 'manual_entry';
    if (filter === 'wearable') return item.source === 'wearable_sync';
    return true;
  });

  const groupByDate = (items) => {
    const groups = {};
    items.forEach(item => {
      const date = new Date(item.timestamp).toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'short', 
        day: 'numeric' 
      });
      if (!groups[date]) groups[date] = [];
      groups[date].push(item);
    });
    return groups;
  };

  const groupedHistory = groupByDate(filteredHistory);

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="rounded-full"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-lg">Scan History</h1>
              <p className="text-xs text-muted-foreground">Your biometric capture timeline</p>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* Filter Tabs */}
      <div className="px-4 py-3 border-b border-border overflow-x-auto">
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'facial', label: 'Facial', icon: Camera },
            { key: 'manual', label: 'Manual', icon: Edit3 },
            { key: 'wearable', label: 'Wearable', icon: Watch }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.key}
                variant={filter === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(tab.key)}
                className={`rounded-full whitespace-nowrap ${filter === tab.key ? 'bg-primary' : ''}`}
                data-testid={`filter-${tab.key}`}
              >
                {Icon && <Icon size={14} className="mr-1" />}
                {tab.label}
              </Button>
            );
          })}
        </div>
      </div>

      <main className="px-4 py-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-pulse text-muted-foreground">Loading history...</div>
          </div>
        ) : filteredHistory.length === 0 ? (
          <Card className="glass-card p-8 text-center">
            <Clock size={40} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-semibold mb-2">No records found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Start capturing your biometrics to see your history here.
            </p>
            <Button
              onClick={() => navigate('/scan/start')}
              className="bg-primary hover:bg-primary/90 rounded-full"
            >
              <Camera size={16} className="mr-2" />
              Start Scan
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedHistory).map(([date, items], groupIndex) => (
              <motion.div
                key={date}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: groupIndex * 0.1 }}
              >
                <h2 className="font-heading font-semibold text-sm text-muted-foreground mb-3">
                  {date}
                </h2>
                <div className="space-y-3">
                  {items.map((item, index) => {
                    const Icon = item.sourceConfig.icon;
                    return (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card 
                          className={`glass-card p-4 ${!item.success ? 'border-l-4 border-l-[#D54B48]' : ''}`}
                          data-testid={`history-item-${item.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div 
                              className={`p-2 rounded-xl ${item.success ? '' : 'opacity-50'}`}
                              style={{ backgroundColor: `${item.sourceConfig.color}20` }}
                            >
                              <Icon size={18} style={{ color: item.sourceConfig.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{item.sourceConfig.label}</span>
                                {item.success ? (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    item.confidence === 'high' 
                                      ? 'bg-[#8BB89F]/20 text-[#8BB89F]' 
                                      : 'bg-[#F8B64C]/20 text-[#F8B64C]'
                                  }`}>
                                    {item.confidence === 'high' ? 'High' : 'Medium'}
                                  </span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#D54B48]/20 text-[#D54B48]">
                                    Failed
                                  </span>
                                )}
                              </div>
                              
                              {item.success && item.metrics && (
                                <div className="flex gap-4 mt-2">
                                  <div className="flex items-center gap-1">
                                    <Heart size={12} className="text-[#D54B48]" />
                                    <span className="text-xs text-muted-foreground">{item.metrics.heart_rate} bpm</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Activity size={12} className="text-[#F8B64C]" />
                                    <span className="text-xs text-muted-foreground">{item.metrics.hrv} ms</span>
                                  </div>
                                </div>
                              )}
                              
                              {!item.success && (
                                <p className="text-xs text-[#D54B48] mt-1">{item.errorReason}</p>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <span className="text-xs text-muted-foreground">
                                {getTimeAgo(item.timestamp)}
                              </span>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {new Date(item.timestamp).toLocaleTimeString('en-US', { 
                                  hour: 'numeric', 
                                  minute: '2-digit' 
                                })}
                              </p>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Quick Actions FAB */}
      <div className="fixed bottom-24 right-4 flex flex-col gap-2">
        <Button
          onClick={() => navigate('/scan/start')}
          className="rounded-full w-14 h-14 bg-primary hover:bg-primary/90 shadow-lg"
          data-testid="fab-scan"
        >
          <Camera size={22} />
        </Button>
      </div>

      <BottomNav />
    </div>
  );
}
